import {
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
  type ReactNode,
  type Ref,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { Button } from './Button';
import { IconButton } from './Button';
import { Icon, type IconName } from '../icons';
import { Field, Input } from './Input';

// Overlays: Portal + focus management + anchored positioning, then Dialog, ConfirmDialog,
// Drawer, Popover, DropdownMenu and Tooltip on top. No third-party kit: every overlay
// traps focus (dialog/drawer), restores it on close, closes on Esc, and is announced.

export function Portal({ children }: { children: ReactNode }) {
  if (typeof document === 'undefined') return null;
  return createPortal(children, document.body);
}

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type=hidden]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/** Keeps Tab inside `ref`, focuses the first focusable (or [data-autofocus]) on open, restores focus on close. */
export function useFocusTrap(ref: RefObject<HTMLElement | null>, active: boolean) {
  useEffect(() => {
    if (!active) return;
    const root = ref.current;
    if (!root) return;
    const prev = document.activeElement as HTMLElement | null;
    const auto = root.querySelector<HTMLElement>('[data-autofocus]') ?? root.querySelector<HTMLElement>(FOCUSABLE);
    // A popover is `visibility: hidden` until it has measured where to sit (useAnchorPosition), and a
    // hidden element cannot take focus — so focus now AND once more on the next frame, when it shows.
    const focusIn = () => {
      if (!root.contains(document.activeElement)) (auto ?? root).focus({ preventScroll: true });
    };
    focusIn();
    const raf = requestAnimationFrame(focusIn);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const items = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    root.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(raf);
      root.removeEventListener('keydown', onKey);
      prev?.focus?.({ preventScroll: true });
    };
  }, [ref, active]);
}

/** Layered Esc handling: only the top-most overlay closes on Escape. */
const stack: number[] = [];
let seq = 0;
export function useEscape(active: boolean, onClose: (() => void) | undefined) {
  useEffect(() => {
    if (!active || !onClose) return;
    const id = ++seq;
    stack.push(id);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && stack[stack.length - 1] === id) {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      const i = stack.indexOf(id);
      if (i >= 0) stack.splice(i, 1);
    };
  }, [active, onClose]);
}

/** Locks body scroll while an overlay is open. */
// Overlays can nest (a confirm inside a drawer), so the lock is counted: the last one out
// restores scrolling. The body class is the signal other layers key off — on a phone the Nova
// assistant hides while a sheet is open rather than floating over its fields (mobile.css).
let scrollLocks = 0;
function useScrollLock(active: boolean) {
  useEffect(() => {
    if (!active) return;
    const prev = document.body.style.overflow;
    scrollLocks += 1;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('hf-modal-open');
    return () => {
      scrollLocks -= 1;
      if (scrollLocks <= 0) {
        scrollLocks = 0;
        document.body.style.overflow = prev;
        document.body.classList.remove('hf-modal-open');
      }
    };
  }, [active]);
}

// ── Dialog ────────────────────────────────────────────────────────────────
export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'md' | 'lg' | 'xl';
  /** Disable closing via Esc/scrim (e.g. while a mutation is in flight). */
  locked?: boolean;
  role?: 'dialog' | 'alertdialog';
}

export function Dialog({ open, onClose, title, description, children, footer, size = 'md', locked, role = 'dialog' }: DialogProps) {
  const ref = useRef<HTMLDivElement>(null);
  const id = useId();
  const close = useCallback(() => {
    if (!locked) onClose();
  }, [locked, onClose]);
  useFocusTrap(ref, open);
  useEscape(open, close);
  useScrollLock(open);
  if (!open) return null;
  return (
    <Portal>
      <div className="hf-dialog-root">
        <div className="hf-scrim blur" onMouseDown={(e) => e.target === e.currentTarget && close()} />
        <div
          ref={ref}
          role={role}
          aria-modal="true"
          aria-labelledby={title ? `${id}-t` : undefined}
          aria-describedby={description ? `${id}-d` : undefined}
          className={['hf-dialog', size !== 'md' && size].filter(Boolean).join(' ')}
          tabIndex={-1}
        >
          {(title || description) && (
            <div className="hf-dialog-head">
              <div>
                {title && <h2 id={`${id}-t`}>{title}</h2>}
                {description && <p id={`${id}-d`}>{description}</p>}
              </div>
              <IconButton icon="x" label="Close" size="sm" onClick={close} disabled={locked} />
            </div>
          )}
          <div className="hf-dialog-body">{children}</div>
          {footer && <div className="hf-dialog-foot">{footer}</div>}
        </div>
      </div>
    </Portal>
  );
}

export interface ConfirmSpec {
  title: string;
  /** What happens, in plain words — including what cannot be undone. */
  body: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  danger?: boolean;
  /** Ask for a reason; when 'required', confirm stays disabled until one is typed. */
  reason?: 'optional' | 'required';
  reasonLabel?: string;
}

export function ConfirmDialog({
  open,
  spec,
  busy = false,
  onConfirm,
  onClose,
}: {
  open: boolean;
  spec: ConfirmSpec;
  busy?: boolean;
  onConfirm: (reason: string | undefined) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) setReason('');
  }, [open]);
  const needsReason = spec.reason === 'required' && !reason.trim();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy || needsReason) return;
    onConfirm(reason.trim() || undefined);
  };
  return (
    <Dialog open={open} onClose={onClose} title={spec.title} locked={busy} role="alertdialog">
      <form onSubmit={submit} className="hf-stack">
        <div className="hf-dim" style={{ fontSize: 'var(--t-13)', lineHeight: 1.5 }}>
          {spec.body}
        </div>
        {spec.reason && (
          <Field label={spec.reasonLabel ?? 'Reason'} optional={spec.reason === 'optional'} required={spec.reason === 'required'}>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} data-autofocus placeholder={spec.reason === 'required' ? 'Required' : ''} />
          </Field>
        )}
        <div className="hf-form-actions" style={{ borderTop: 0, paddingTop: 4 }}>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {spec.cancelLabel ?? 'Cancel'}
          </Button>
          <Button type="submit" variant={spec.danger ? 'danger' : 'primary'} solid={spec.danger} busy={busy} disabled={needsReason} data-autofocus={!spec.reason || undefined}>
            {spec.confirmLabel}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ── Drawer ────────────────────────────────────────────────────────────────
export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  side?: 'right' | 'left';
  size?: 'md' | 'lg';
  locked?: boolean;
  headerExtra?: ReactNode;
}

export function Drawer({ open, onClose, title, description, children, footer, side = 'right', size = 'md', locked, headerExtra }: DrawerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const id = useId();
  const close = useCallback(() => {
    if (!locked) onClose();
  }, [locked, onClose]);
  useFocusTrap(ref, open);
  useEscape(open, close);
  useScrollLock(open);
  if (!open) return null;
  return (
    <Portal>
      <div className="hf-drawer-root">
        <div className="hf-scrim" onMouseDown={(e) => e.target === e.currentTarget && close()} />
        <div
          ref={ref}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? `${id}-t` : undefined}
          className={['hf-drawer', side === 'left' && 'left', size !== 'md' && size].filter(Boolean).join(' ')}
          tabIndex={-1}
        >
          <div className="hf-drawer-head">
            <div className="hf-grow">
              {title && <h2 id={`${id}-t`}>{title}</h2>}
              {description && <p>{description}</p>}
            </div>
            {headerExtra}
            <IconButton icon="x" label="Close" size="sm" onClick={close} disabled={locked} />
          </div>
          <div className="hf-drawer-body hf-scroll">{children}</div>
          {footer && <div className="hf-drawer-foot">{footer}</div>}
        </div>
      </div>
    </Portal>
  );
}

// ── Anchored positioning ──────────────────────────────────────────────────
export type Placement = 'bottom-start' | 'bottom-end' | 'bottom' | 'top-start' | 'top-end' | 'top' | 'right-start' | 'left-start';

export function useAnchorPosition(anchor: RefObject<HTMLElement | null> | HTMLElement | null, open: boolean, placement: Placement = 'bottom-start', offset = 6) {
  const [style, setStyle] = useState<CSSProperties>({ visibility: 'hidden', top: 0, left: 0 });
  const popRef = useRef<HTMLDivElement>(null);

  const compute = useCallback(() => {
    const el = anchor && 'current' in anchor ? anchor.current : anchor;
    const pop = popRef.current;
    if (!el || !pop) return;
    const a = el.getBoundingClientRect();
    const pw = pop.offsetWidth;
    const ph = pop.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let top = a.bottom + offset;
    let left = a.left;
    let p = placement;
    if (p.startsWith('bottom') && top + ph > vh - 8 && a.top - ph - offset > 8) p = p.replace('bottom', 'top') as Placement;
    if (p.startsWith('top') && a.top - ph - offset < 8 && a.bottom + offset + ph < vh - 8) p = p.replace('top', 'bottom') as Placement;
    if (p.startsWith('top')) top = a.top - ph - offset;
    if (p === 'bottom-end' || p === 'top-end') left = a.right - pw;
    if (p === 'bottom' || p === 'top') left = a.left + a.width / 2 - pw / 2;
    if (p === 'right-start') {
      left = a.right + offset;
      top = a.top;
    }
    if (p === 'left-start') {
      left = a.left - pw - offset;
      top = a.top;
    }
    left = Math.max(8, Math.min(left, vw - pw - 8));
    top = Math.max(8, Math.min(top, vh - ph - 8));
    setStyle({ top, left, visibility: 'visible' });
  }, [anchor, placement, offset]);

  useLayoutEffect(() => {
    if (!open) return;
    compute();
    const ro = typeof ResizeObserver !== 'undefined' && popRef.current ? new ResizeObserver(compute) : null;
    if (ro && popRef.current) ro.observe(popRef.current);
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [open, compute]);

  return { popRef, style };
}

/** Closes when a pointer goes down outside both the popover and its anchor. */
function useClickOutside(active: boolean, refs: (RefObject<HTMLElement | null> | HTMLElement | null)[], onClose: () => void) {
  useEffect(() => {
    if (!active) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      for (const r of refs) {
        const el = r && 'current' in r ? r.current : r;
        if (el && el.contains(t)) return;
      }
      onClose();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [active, refs, onClose]);
}

// ── Popover ───────────────────────────────────────────────────────────────
export interface PopoverProps {
  open: boolean;
  onClose: () => void;
  anchor: RefObject<HTMLElement | null> | HTMLElement | null;
  children: ReactNode;
  placement?: Placement;
  /** Match the anchor's width (comboboxes). */
  matchWidth?: boolean;
  pad?: boolean;
  role?: string;
  trapFocus?: boolean;
  className?: string;
  'aria-label'?: string;
  id?: string;
}

export function Popover({ open, onClose, anchor, children, placement = 'bottom-start', matchWidth, pad, role = 'dialog', trapFocus = false, className, id, ...rest }: PopoverProps) {
  const { popRef, style } = useAnchorPosition(anchor, open, placement);
  useEscape(open, onClose);
  useClickOutside(open, [popRef, anchor], onClose);
  useFocusTrap(popRef, open && trapFocus);
  if (!open) return null;
  const el = anchor && 'current' in anchor ? anchor.current : anchor;
  const width = matchWidth && el ? el.getBoundingClientRect().width : undefined;
  return (
    <Portal>
      <div
        ref={popRef}
        id={id}
        role={role}
        aria-label={rest['aria-label']}
        className={['hf-pop', pad && 'pad', className].filter(Boolean).join(' ')}
        style={{ ...style, ...(width ? { width, minWidth: width } : {}) }}
      >
        {children}
      </div>
    </Portal>
  );
}

// ── Dropdown menu ─────────────────────────────────────────────────────────
export interface MenuItemDef {
  key?: string;
  label: ReactNode;
  icon?: IconName;
  hint?: string;
  danger?: boolean;
  disabled?: boolean;
  onSelect?: () => void;
  /** Section label (renders as a heading, not an item). */
  heading?: boolean;
  separator?: boolean;
  checked?: boolean;
}

/** The keyboard-navigable list body shared by DropdownMenu and ContextMenu. */
export function MenuList({ items, onClose }: { items: MenuItemDef[]; onClose: () => void }) {
  const [active, setActive] = useState(0);
  const selectable = items.map((it, i) => ({ it, i })).filter(({ it }) => !it.heading && !it.separator && !it.disabled);
  const onKey = (e: ReactKeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => (a + 1) % Math.max(1, selectable.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (a - 1 + selectable.length) % Math.max(1, selectable.length));
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const s = selectable[active];
      if (s) {
        s.it.onSelect?.();
        onClose();
      }
    }
  };
  return (
    <div className="hf-menu" onKeyDown={onKey} tabIndex={-1} data-autofocus>
      {items.map((it, i) => {
        if (it.separator) return <div key={it.key ?? i} className="hf-menu-sep" role="separator" />;
        if (it.heading)
          return (
            <div key={it.key ?? i} className="hf-menu-label">
              {it.label}
            </div>
          );
        const idx = selectable.findIndex((s) => s.i === i);
        return (
          <button
            key={it.key ?? i}
            type="button"
            role="menuitem"
            className={['hf-menu-item', it.danger && 'danger', idx === active && 'active'].filter(Boolean).join(' ')}
            disabled={it.disabled}
            onMouseEnter={() => idx >= 0 && setActive(idx)}
            onClick={() => {
              it.onSelect?.();
              onClose();
            }}
          >
            {it.icon && <Icon name={it.icon} size={14} />}
            {it.checked != null && <Icon name={it.checked ? 'check' : 'circle'} size={12} style={{ opacity: it.checked ? 1 : 0.25 }} />}
            <span>{it.label}</span>
            {it.hint && <span className="hint">{it.hint}</span>}
          </button>
        );
      })}
    </div>
  );
}

// ── Context menu: a menu opened at a point (right-click / Menu key) ─────────
export function ContextMenu({ open, x, y, items, onClose, 'aria-label': ariaLabel }: { open: boolean; x: number; y: number; items: MenuItemDef[]; onClose: () => void; 'aria-label'?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<CSSProperties>({ visibility: 'hidden', top: y, left: x });
  useEscape(open, onClose);
  useClickOutside(open, [ref], onClose);
  useFocusTrap(ref, open);
  useLayoutEffect(() => {
    if (!open) return;
    const pop = ref.current;
    if (!pop) return;
    const pw = pop.offsetWidth;
    const ph = pop.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const left = Math.max(8, Math.min(x, vw - pw - 8));
    const top = Math.max(8, Math.min(y, vh - ph - 8));
    setStyle({ top, left, visibility: 'visible' });
  }, [open, x, y]);
  if (!open) return null;
  return (
    <Portal>
      <div ref={ref} role="menu" aria-label={ariaLabel} className="hf-pop hf-context-menu" style={style}>
        <MenuList items={items} onClose={onClose} />
      </div>
    </Portal>
  );
}

export function DropdownMenu({
  trigger,
  items,
  placement = 'bottom-end',
  'aria-label': ariaLabel,
}: {
  /** A single element that receives onClick/aria props (Button, IconButton…). */
  trigger: ReactElement;
  items: MenuItemDef[];
  placement?: Placement;
  'aria-label'?: string;
}) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLElement>(null);
  const id = useId();
  const close = useCallback(() => setOpen(false), []);

  // The trigger may carry its own ref (e.g. a caller anchoring a follow-up popover to the same
  // button): keep it working alongside ours instead of replacing it.
  const ownRef = isValidElement(trigger) ? ((trigger.props as { ref?: Ref<HTMLElement> }).ref ?? null) : null;
  const setRefs = useCallback(
    (el: HTMLElement | null) => {
      anchorRef.current = el;
      if (typeof ownRef === 'function') ownRef(el);
      else if (ownRef && typeof ownRef === 'object') (ownRef as { current: HTMLElement | null }).current = el;
    },
    [ownRef],
  );

  const trig = isValidElement(trigger)
    ? cloneElement(trigger as ReactElement<Record<string, unknown>>, {
        ref: setRefs,
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        'aria-controls': open ? id : undefined,
        onClick: (e: MouseEvent) => {
          (trigger.props as { onClick?: (e: MouseEvent) => void }).onClick?.(e);
          setOpen((v) => !v);
        },
        onKeyDown: (e: ReactKeyboardEvent) => {
          if (e.key === 'ArrowDown' && !open) {
            e.preventDefault();
            setOpen(true);
          }
        },
      })
    : trigger;

  return (
    <>
      {trig}
      <Popover open={open} onClose={close} anchor={anchorRef} placement={placement} role="menu" id={id} aria-label={ariaLabel} trapFocus>
        <MenuList items={items} onClose={close} />
      </Popover>
    </>
  );
}

// ── Tooltip ───────────────────────────────────────────────────────────────
export function Tooltip({ content, kbd, children, placement = 'top', delay = 350 }: { content: ReactNode; kbd?: string; children: ReactElement; placement?: Placement; delay?: number }) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLElement>(null);
  const timer = useRef<number | null>(null);
  const { popRef, style } = useAnchorPosition(anchorRef, open, placement, 6);
  const show = () => {
    timer.current = window.setTimeout(() => setOpen(true), delay);
  };
  const hide = () => {
    if (timer.current) window.clearTimeout(timer.current);
    setOpen(false);
  };
  const child = isValidElement(children)
    ? cloneElement(children as ReactElement<Record<string, unknown>>, {
        ref: anchorRef,
        onMouseEnter: (e: MouseEvent) => {
          (children.props as { onMouseEnter?: (e: MouseEvent) => void }).onMouseEnter?.(e);
          show();
        },
        onMouseLeave: (e: MouseEvent) => {
          (children.props as { onMouseLeave?: (e: MouseEvent) => void }).onMouseLeave?.(e);
          hide();
        },
        onFocus: (e: FocusEvent) => {
          (children.props as { onFocus?: (e: FocusEvent) => void }).onFocus?.(e);
          show();
        },
        onBlur: (e: FocusEvent) => {
          (children.props as { onBlur?: (e: FocusEvent) => void }).onBlur?.(e);
          hide();
        },
      })
    : children;
  return (
    <>
      {child}
      {open && (
        <Portal>
          <div ref={popRef} className="hf-tooltip" role="tooltip" style={style}>
            {content}
            {kbd && <kbd>{kbd}</kbd>}
          </div>
        </Portal>
      )}
    </>
  );
}

// ── Confirm-as-a-hook: `const confirm = useConfirm(); if (await confirm({...})) …` ──
type Pending = { spec: ConfirmSpec; resolve: (v: string | false) => void } | null;
const ConfirmCtx = createContext<((spec: ConfirmSpec) => Promise<string | false>) | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending>(null);
  const confirm = useCallback((spec: ConfirmSpec) => new Promise<string | false>((resolve) => setPending({ spec, resolve })), []);
  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      {pending && (
        <ConfirmDialog
          open
          spec={pending.spec}
          onConfirm={(reason) => {
            pending.resolve(reason ?? '');
            setPending(null);
          }}
          onClose={() => {
            pending.resolve(false);
            setPending(null);
          }}
        />
      )}
    </ConfirmCtx.Provider>
  );
}

/** Returns false when cancelled, else the (possibly empty) reason string. */
export function useConfirm() {
  const c = useContext(ConfirmCtx);
  if (!c) throw new Error('useConfirm requires <ConfirmProvider>');
  return c;
}
